import DimensionsService from './dimensionsService';
import plotsService from './plotsService';
import { getRumNameFromResource } from '@splunk/olly-imm/build/detectors/utils/rumFunctions';
export const APM_IDENTIFIERS = ['ep_above_ft', 'd_above_ft', 'signal_baseline'];
export const APM_ALERT_TYPES = ['errors', 'latency', 'requests'];
import { capitalize } from 'lodash';
import { SCALING_UNIT_TYPES } from '../../../../common/ui/formatting/scalingUnitTypes';

const UNIT_ABBREVIATIONS = {
    [SCALING_UNIT_TYPES.MILLISECOND]: 'ms',
    [SCALING_UNIT_TYPES.PERCENT]: '%',
    [SCALING_UNIT_TYPES.REQUESTS_PER_SECOND]: 'req/s',
};

export default class ChartInformationService {
    constructor(detectorPriorityService, valueFormatter, signalTypeService, featureEnabled) {
        this.detectorPriorityService = detectorPriorityService;
        this.valueFormatter = valueFormatter;
        this.signalTypeService = signalTypeService;
        this.featureEnabled = featureEnabled;
        this.dimensionsService = new DimensionsService(featureEnabled);
    }

    getChartInformation(event, incidentInfo, orgId, plotColorMap) {
        const incidentRelatedPlots = plotsService.getPlotsForIncident(event, incidentInfo);
        const triggerValue = plotsService.getTriggerValue(
            incidentRelatedPlots,
            this.valueFormatter.formatValue
        );
        const unitSuffix = this._getUnitSuffix(event);
        return {
            swatchClass: this._getSwatchClass(event.metadata.sf_severity),
            title: this.buildChartTitle(event, incidentInfo, incidentRelatedPlots),
            plots: plotsService.getPlotsInformation(
                incidentRelatedPlots,
                event,
                this.valueFormatter.formatValue,
                plotColorMap
            ),
            condition: event.metadata.sf_detectOnCondition,
            triggerValue: triggerValue,
            dimensions: this.dimensionsService.getForPlots(incidentRelatedPlots, orgId),
            unitSuffix: unitSuffix,
        };
    }

    buildChartTitle(event, incidentInfo, relatedPlots) {
        if (event?.properties?.detector_type === 'rum') {
            return this.buildForRUMDetector(event, relatedPlots);
        }

        if (event?.metadata?.sf_eventSloId) {
            return this.buildForSlo(event, relatedPlots);
        }

        const isAPMTypeDetector =
            event?.properties?.detector_type === 'apm' &&
            APM_ALERT_TYPES.includes(event?.properties?.alert_type);
        // hasAPMIdentifiers can be removed after APM detectors migration
        const hasAPMIdentifiers =
            incidentInfo && APM_IDENTIFIERS.some((id) => id === incidentInfo.conditionIdentifier);
        if (isAPMTypeDetector || hasAPMIdentifiers) {
            return this.buildForAPMDetector(event, relatedPlots);
        }
        return this.buildForInfrastructureDetector(relatedPlots, incidentInfo);
    }

    getValueString(value, threshold, suffix = '') {
        if (value && threshold) {
            return `(value: ${value}${suffix}) > ${threshold}${suffix}`;
        }
        return '';
    }

    buildForSlo(event, relatedPlots) {
        function getSloAlertTypeFromAlertName(alertType) {
            switch (alertType) {
                case 'BREACH':
                    return 'Breach';
                case 'ERROR_BUDGET_LEFT':
                    return 'Error Budget Left';
                case 'BURN_RATE':
                    return 'Burn Rate';
                default:
                    return `${capitalize(alertType)}`;
            }
        }
        const value = plotsService.getTriggerValue(relatedPlots);
        const threshold = plotsService.getSloThresholdValue(relatedPlots);
        return `SLO ${getSloAlertTypeFromAlertName(event.metadata.sf_eventSloAlertType)} Event ${
            value && threshold ? `(value: ${value}) < ${threshold}` : ''
        }`;
    }

    buildForAPMDetector(event, relatedPlots) {
        const service = event.metadata[this.signalTypeService.getAPMDimensions().service];
        const value = plotsService.getTriggerValue(relatedPlots);
        const threshold = plotsService.getThresholdValue(relatedPlots);
        const environment = event.metadata[this.signalTypeService.getAPMDimensions().environment];
        const workflow = event.metadata[this.signalTypeService.getAPMDimensions().workflow];
        const useTransactionTerminology = this.featureEnabled(
            'apm2RenameWorkflowToTransactionInUI'
        );
        const workflowLabel = useTransactionTerminology ? 'Business Transaction' : 'Workflow';

        const buildTitle = (type, alertType, service, environment, value, threshold) => {
            switch (alertType) {
                case 'latency':
                    return `APM ${type} Latency - ${service}:${environment} ${this.getValueString(
                        value,
                        threshold,
                        UNIT_ABBREVIATIONS[SCALING_UNIT_TYPES.MILLISECOND]
                    )}`;
                case 'requests':
                    return `APM ${type} Requests - ${service}:${environment} ${this.getValueString(
                        value,
                        threshold,
                        this.featureEnabled('alertModalShowUnits')
                            ? UNIT_ABBREVIATIONS[SCALING_UNIT_TYPES.REQUESTS_PER_SECOND]
                            : ''
                    )}`;
                case 'errors':
                    return `APM ${type} Error rate - ${service}:${environment} ${this.getValueString(
                        value,
                        threshold,
                        UNIT_ABBREVIATIONS[SCALING_UNIT_TYPES.PERCENT]
                    )}`;
                default:
                    return `APM ${type} - ${service}:${environment} ${this.getValueString(
                        value,
                        threshold
                    )}`;
            }
        };

        if (workflow) {
            return buildTitle(
                workflowLabel,
                event.properties.alert_type,
                workflow,
                environment,
                value,
                threshold
            );
        }
        return buildTitle(
            'Service',
            event.properties.alert_type,
            service,
            environment,
            value,
            threshold
        );
    }

    buildForRUMDetector(event, relatedPlots) {
        const alert_type = event.properties.alert_type;
        const suffix =
            alert_type === 'latency'
                ? UNIT_ABBREVIATIONS[SCALING_UNIT_TYPES.MILLISECOND]
                : alert_type === 'error_rate'
                ? UNIT_ABBREVIATIONS[SCALING_UNIT_TYPES.PERCENT]
                : '';
        const value = plotsService.getTriggerValue(relatedPlots, this.valueFormatter.formatValue);
        const threshold = plotsService.getThresholdValue(relatedPlots);
        const rumFunctionLabel = getRumNameFromResource(event.properties.resource_type, alert_type);
        return `RUM ${rumFunctionLabel} ${this.getValueString(value, threshold, suffix)}`;
    }

    buildForInfrastructureDetector(relatedPlots, incidentInfo) {
        if (incidentInfo && incidentInfo.builtInFunction && incidentInfo.readableCondition) {
            return incidentInfo.readableCondition;
        }

        const metricName = plotsService.getTriggerPlotName(relatedPlots);
        let value = plotsService.getTriggerValue(relatedPlots, this.valueFormatter.formatValue);
        if (value === '-') {
            value = 'N/A';
        }

        return metricName && value ? `${metricName} (value: ${value})` : '';
    }

    _getUnitSuffix(event) {
        const apmConfig = this.getAPMChartUnitConfig(event);
        if (!apmConfig || !apmConfig.unitType) {
            return '';
        }
        return UNIT_ABBREVIATIONS[apmConfig.unitType] || '';
    }

    _getSwatchClass(severityName) {
        const severity = this.detectorPriorityService.getSeverityByDisplay(severityName);

        return this.detectorPriorityService.getSwatchClassBySeverity(severity);
    }

    /**
     * Returns unit configuration for APM detector alerts based on alert_type.
     * Returns null if the event is not from an APM detector.
     */
    getAPMChartUnitConfig(event) {
        if (!this.featureEnabled('alertModalShowUnits')) {
            return null;
        }

        const isAPMTypeDetector =
            event?.properties?.detector_type === 'apm' &&
            APM_ALERT_TYPES.includes(event?.properties?.alert_type);

        if (!isAPMTypeDetector) {
            return null;
        }

        switch (event.properties.alert_type) {
            case 'latency':
                return { unitType: SCALING_UNIT_TYPES.MILLISECOND };
            case 'errors':
                return { unitType: SCALING_UNIT_TYPES.PERCENT };
            case 'requests':
                return { unitType: SCALING_UNIT_TYPES.REQUESTS_PER_SECOND };
            default:
                return null;
        }
    }
}
